import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Trophy, Sparkles, Star, Zap, Crown } from 'lucide-react';

interface ConfettiExplosionProps {
  isVisible: boolean;
  tier?: 'standard' | 'legendary';
  title?: string;
  subtitle?: string;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  color: string;
  shape: 'rect' | 'circle' | 'star' | 'trophy' | 'sparkle' | 'crown';
  duration: number;
  delay: number;
}

const COLORS_STANDARD = [
  '#a3e635', // lime-400
  '#facc15', // yellow-400
  '#fb923c', // orange-400
  '#f43f5e', // rose-500
  '#c084fc', // purple-400
  '#38bdf8', // sky-400
  '#34d399', // emerald-400
  '#f472b6', // pink-400
];

const COLORS_LEGENDARY = [
  '#fbbf24', // amber-400 gold
  '#f59e0b', // amber-500
  '#eab308', // yellow-500
  '#a855f7', // purple-500
  '#ec4899', // pink-500
  '#38bdf8', // sky-400 diamond
  '#ffffff', // pure white shine
  '#a3e635', // lime
];

export const ConfettiExplosion: React.FC<ConfettiExplosionProps> = ({ 
  isVisible,
  tier = 'standard',
  title,
  subtitle
}) => {
  const isLegendary = tier === 'legendary';
  const particleCount = isLegendary ? 110 : 70;
  const colors = isLegendary ? COLORS_LEGENDARY : COLORS_STANDARD;

  // Generate randomized particles for the explosion
  const particles = useMemo(() => {
    const list: Particle[] = [];
    const shapes: Particle['shape'][] = ['rect', 'circle', 'star', 'trophy', 'sparkle', 'crown'];

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = isLegendary ? 200 + Math.random() * 700 : 150 + Math.random() * 550;
      
      list.push({
        id: i,
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance + (100 + Math.random() * 250),
        rotation: (Math.random() - 0.5) * (isLegendary ? 1080 : 720),
        scale: isLegendary ? 0.8 + Math.random() * 1.4 : 0.6 + Math.random() * 1.0,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        duration: isLegendary ? 2.5 + Math.random() * 2.0 : 1.8 + Math.random() * 1.6,
        delay: Math.random() * 0.25,
      });
    }
    return list;
  }, [isVisible, isLegendary, particleCount]);

  const defaultTitle = isLegendary ? 'LEGENDARY UNLOCKED!' : 'LEVEL UP!';
  const defaultSub = isLegendary ? '⭐ Ultimate Playground Achievement! ⭐' : '⭐ New Athlete Tier Reached! ⭐';

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 pointer-events-none z-[100] flex items-center justify-center overflow-hidden">
          {/* Central Burst Flash Banner */}
          <motion.div
            initial={{ scale: 0.1, opacity: 0, y: 60 }}
            animate={{ scale: 1.15, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: -40 }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 350, damping: 22 }}
            className={`absolute top-1/4 flex flex-col items-center justify-center px-8 py-5 rounded-3xl shadow-[0_0_60px_rgba(251,191,36,0.8)] border-4 pointer-events-none ${
              isLegendary
                ? 'bg-gradient-to-r from-amber-500 via-yellow-300 to-purple-600 text-black border-amber-200'
                : 'bg-gradient-to-r from-amber-500/90 via-yellow-400/95 to-lime-400/90 text-black border-white/40'
            }`}
          >
            <div className="flex items-center space-x-3">
              <Crown className="w-9 h-9 fill-amber-900 text-amber-900 animate-bounce" />
              <h2 className="text-3xl sm:text-4xl font-black italic uppercase tracking-wider text-black drop-shadow-md">
                {title || defaultTitle}
              </h2>
              <Trophy className="w-9 h-9 fill-amber-900 text-amber-900 animate-bounce" />
            </div>
            <p className="text-xs sm:text-sm font-black uppercase tracking-widest text-black/90 font-mono mt-1">
              {subtitle || defaultSub}
            </p>
          </motion.div>

          {/* Confetti Particles */}
          <div className="relative w-0 h-0">
            {particles.map((p) => (
              <motion.div
                key={p.id}
                initial={{
                  x: 0,
                  y: 0,
                  scale: 0,
                  rotate: 0,
                  opacity: 1,
                }}
                animate={{
                  x: p.x,
                  y: p.y,
                  scale: [0, p.scale, p.scale * 0.8, 0],
                  rotate: p.rotation,
                  opacity: [0, 1, 1, 0],
                }}
                transition={{
                  duration: p.duration,
                  delay: p.delay,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                style={{
                  position: 'absolute',
                  color: p.color,
                }}
              >
                {p.shape === 'rect' && (
                  <div
                    className="w-3.5 h-4.5 rounded-sm shadow-sm"
                    style={{ backgroundColor: p.color }}
                  />
                )}
                {p.shape === 'circle' && (
                  <div
                    className="w-3.5 h-3.5 rounded-full shadow-sm"
                    style={{ backgroundColor: p.color }}
                  />
                )}
                {p.shape === 'star' && <Star className="w-6 h-6 fill-current stroke-none" />}
                {p.shape === 'trophy' && <Trophy className="w-7 h-7 fill-current stroke-none" />}
                {p.shape === 'sparkle' && <Sparkles className="w-6 h-6 fill-current stroke-none" />}
                {p.shape === 'crown' && <Crown className="w-7 h-7 fill-current stroke-none" />}
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};
